// /onboarding — re-enter the setup wizard on purpose.
//
// WHY THIS EXISTS. Onboarding is rendered by ProtectedRoute, which wraps every protected
// route, so while it is showing no URL escapes it — and once "Skip for now" writes
// `skipped_at`, is_onboarding_complete answers true and ProtectedRoute never renders the
// wizard again. There was no route that could: the app defined `/onboarding-test` and no
// `/onboarding`. Skipping was therefore a one-way door, and clearing the onboarding_*
// localStorage keys did not help, because the fact lives in company_settings. Only a fresh
// signup got back in.
//
// Skipping SHOULD be easy and it should not be permanent. Jobber and HouseCall Pro both
// keep setup resumable from a checklist after you dismiss it; dismissing a wizard is not
// the same as declaring the work done, which is exactly why the skip writes `skipped_at`
// rather than `completed_at`.
//
// This route renders the same wizard deliberately, so nothing about the automatic gate
// changes: ProtectedRoute still decides who is FORCED into onboarding, and this decides
// who may CHOOSE to go back to it.
//
// RECOVERY MUST MAKE THE SAME MODE CHOICE THE FORCED FLOW DOES. The wizard runs [1,2,6]
// for Quick and all six for Full, and that choice is COMPANY-scoped: ProtectedRoute picks
// it via AssessmentStep and stores it in `onboarding_mode_<companyId>`, then passes it to
// the wizard EXPLICITLY. Rendering the wizard here with no prop dropped it onto the
// UNSCOPED `onboarding_quickStartMode` key instead — a leftover as likely to be another
// company's as this one's, and absent for anyone who skipped before choosing a path, so
// recovery always fell to the six-step Full wizard with no way to pick Quick (finding
// #309). So resolve the company's own choice first; if it has none, ask the same two
// questions ProtectedRoute asks and pass the answer through explicitly.

import React, { useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { supabase } from '../utils/supabaseClient';
import OnboardingWizard from '../components/OnboardingWizard';
import AssessmentStep from '../components/Onboarding/AssessmentStep';

const ResumeOnboarding = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useUser();

  // Both exits go to the neutral /dashboard rather than /admin-dashboard, which
  // DashboardRouter treats as an explicit view choice that overrides the user's saved
  // preference (see Login.js).
  const leave = () => navigate('/dashboard');

  // Company-scoped mode, keyed exactly as ProtectedRoute keys it so the two routes read and
  // write the same fact.
  const modeKey = user?.company_id ? `onboarding_mode_${user.company_id}` : null;
  const [mode, setMode] = useState(null); // 'quick' | 'complete' | null
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!user?.company_id) return undefined;

    const resolve = async () => {
      // A prior run's company-scoped choice, if it survived in this browser.
      let chosen = null;
      try {
        const saved = localStorage.getItem(modeKey);
        if (saved === 'quick' || saved === 'complete') chosen = saved;
      } catch { /* private mode: no persistence */ }

      // The database is the durable record of both facts localStorage cannot be trusted for
      // here: whether setup is already finished (a completed company must still exit to
      // /dashboard, not be asked to choose again) and, when localStorage was cleared, which
      // path AssessmentStep persisted for this company (`assessment.chosen_mode`).
      try {
        const { data } = await supabase
          .from('company_settings')
          .select('onboarding_progress')
          .eq('company_id', user.company_id)
          .maybeSingle();
        if (cancelled) return;
        const progress = data?.onboarding_progress || null;
        if (progress?.completed_at) { leave(); return; }
        if (!chosen) {
          const priorMode = progress?.assessment?.chosen_mode;
          if (priorMode === 'quick' || priorMode === 'complete') chosen = priorMode;
        }
      } catch { /* read failed: fall back to whatever localStorage gave */ }

      if (cancelled) return;
      if (chosen) setMode(chosen);
      setResolved(true);
    };

    resolve();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.company_id]);

  // AssessmentStep already persists the choice to onboarding_progress.assessment.chosen_mode;
  // mirror ProtectedRoute and also write the company-scoped localStorage key so an immediate
  // reload resolves the same path without waiting on the DB round trip.
  const choose = (m) => {
    if (m === 'quick' || m === 'complete') {
      try { if (modeKey) localStorage.setItem(modeKey, m); }
      catch { /* private mode: in-memory only */ }
      setMode(m);
    }
  };

  // This route sits outside ProtectedRoute on purpose, so it must send a signed-out visitor to
  // login itself — it waited for a company id that never comes and spun forever (#4143).
  if (!authLoading && !user) {
    return <Navigate to={`/login?redirect=${encodeURIComponent('/onboarding')}`} replace />;
  }

  // Wait for company identity and the resolve() attempt before deciding what to render, so
  // the chooser cannot flash before a resolved mode (or a completed exit) is known.
  if (!user?.company_id || !resolved) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // No company-specific choice on record: ask the same two questions the forced first-run
  // flow asks, rather than silently defaulting to Full.
  if (!mode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4 py-10">
        <AssessmentStep onChoose={choose} onSkip={leave} />
      </div>
    );
  }

  return (
    <OnboardingWizard
      onComplete={leave}
      onSkip={leave}
      quickStartMode={mode === 'quick'}
    />
  );
};

export default ResumeOnboarding;
